import { ChannelState, PaginationInfo } from './state';
import { ActionTree } from 'vuex';
import { normalize } from 'normalizr';
import RootState from '@/store/root_state';
import { forumQuery } from '@/helpers/graphql';
import {
  channelInfoByIdQuery,
  paginatedChannelInfoByIdQuery,
} from '@/store/queries/forumAPIQueries';
import { channel as channelSchema } from '../forum_api_normalizr_schema';
import SentryClient from '@/helpers/sentry';
import { ChannelModel } from '@/models/forumAPI/ChannelModel';
import { DisciplinesForumChannelsService } from '@/services/undergrad/pegasus-epona/disciplines-forum-channels.service';

const actions: ActionTree<ChannelState, RootState> = {
  async PULL_FORUM_API_CHANNELS(
    { commit, dispatch },
    { courseId, disciplinesIds }: { courseId: string; disciplinesIds: any },
  ) {
    commit('SET_FORUM_API_CHANNELS', []);

    try {
      if (!disciplinesIds.length) {
        return;
      }

      const disciplinesChannel = await DisciplinesForumChannelsService.getDisciplinesForumChannels(
        courseId,
        disciplinesIds,
      );

      const normalizedResponse = normalize(
        { channels: disciplinesChannel },
        {
          channels: [channelSchema],
        },
      );

      commit('SET_FORUM_API_CHANNELS', normalizedResponse.entities);
      commit('SET_DISCIPLINES_INSTANCES_IDS', disciplinesIds);

      disciplinesChannel.forEach((disciplineChannel: ChannelModel) =>
        dispatch('PULL_FORUM_API_CHANNEL_INFO', { channelId: disciplineChannel.id }),
      );
    } catch (error) {
      SentryClient.captureError(error);
    }
  },
  async PULL_FORUM_API_CHANNEL_INFO(
    { commit },
    {
      channelId,
      orderByDesc,
      first,
      skip,
    }: {
      channelId: number;
      orderByDesc: boolean | undefined;
      first: number | undefined;
      skip: number | undefined;
    },
  ) {
    if (!channelId) return;

    const isPaginated = orderByDesc !== undefined && first !== undefined && skip !== undefined;

    if (isPaginated) {
      const response = await forumQuery(paginatedChannelInfoByIdQuery, {
        channelId,
        mostRecentFirst: orderByDesc,
        first,
        skip,
      });

      if (!response.data.paginatedDiscussions) return;

      const { totalCount, discussions } = response.data.paginatedDiscussions;

      const paginationData: PaginationInfo = { numberOfItems: totalCount };

      const channelData = {
        channel: {
          discussions,
        },
      };
      const normalizedResponse = normalize(channelData, {
        channel: channelSchema,
      });

      commit('SET_FORUM_API_PAGINATION_INFO', paginationData);
      commit('SET_FORUM_API_PAGINATED_DISCUSSIONS', normalizedResponse.entities);
      commit('SET_FORUM_API_COMMENTS', normalizedResponse.entities);
      return;
    }

    const response = await forumQuery(channelInfoByIdQuery, { channelId });

    const normalizedResponse = normalize(response.data, {
      channel: channelSchema,
    });

    commit('SET_FORUM_API_DISCUSSIONS', normalizedResponse.entities);
    commit('SET_FORUM_API_COMMENTS', normalizedResponse.entities);
  },
};

export default actions;
